import { getApiClient } from '../composables/useApiClient'
import { TrackingEventType } from '@monorepo/types'
import { TrackingRetryStrategy } from './tracking/retry-strategy'
import { CircuitBreaker } from './tracking/circuit-breaker'
import { QueuePersistence } from './tracking/queue-persistence'
import { RequestDeduplication } from './tracking/request-deduplication'
import { TrackingMetrics } from './tracking/tracking-metrics'
import type {
  BaseTrackingEvent,
  CTAClickEvent,
  OnboardingEvent,
  WizardEvent,
  MonetizationEvent,
  EngagementEvent,
  PerformanceEvent,
  ExperimentEvent,
  TrackEventPayload
} from '@monorepo/types'


export type TrackingEvent = CTAClickEvent | OnboardingEvent | WizardEvent | MonetizationEvent | EngagementEvent | PerformanceEvent | ExperimentEvent

/**
 * Tracking Service with Resilience Patterns
 *
 * Implements:
 * - Exponential backoff with jitter for retries
 * - Circuit breaker pattern to prevent cascading failures
 * - Request timeout to prevent hangs
 * - Fingerprint race condition fix
 * - Queue persistence (Phase 2)
 * - Request deduplication (Phase 3)
 */
export class TrackingService {
  private _apiClient: ReturnType<typeof getApiClient> | null = null
  private queue: TrackingEvent[] = []
  private isOnline = navigator.onLine
  private batchSize = 10
  private flushInterval = 5000 // 5 seconds
  private maxQueueSize = 5000 // ⚠️ Prevent unbounded queue growth (was causing 313k+ events)
  private initialized = false
  private _fingerprint: string | null = null
  private fingerprintPromise: Promise<string> | null = null

  // Phase 1: Resilience patterns
  private retryStrategy: TrackingRetryStrategy
  private circuitBreaker: CircuitBreaker
  private requestTimeout = 30000 // 30 seconds

  // Phase 2: Queue persistence
  private persistence: QueuePersistence<TrackingEvent> | null = null
  private persistenceInitPromise: Promise<void> | null = null

  // Phase 3: Request deduplication and metrics
  private deduplication: RequestDeduplication
  private metrics: TrackingMetrics

  constructor() {
    // Initialize resilience patterns
    this.retryStrategy = new TrackingRetryStrategy({
      baseDelay: 1000,
      maxDelay: 30000,
      maxAttempts: 5,
      enableJitter: true,
    })

    this.circuitBreaker = new CircuitBreaker({
      failureThreshold: 3,
      timeout: 60000, // 60 seconds before attempting recovery
      autoTransition: true,
    })

    // Initialize queue persistence if IndexedDB is available
    if (QueuePersistence.isAvailable()) {
      this.persistence = new QueuePersistence<TrackingEvent>({
        dbName: 'tracking-db',
        storeName: 'events',
        version: 1,
      })
    }

    // Phase 3: Initialize deduplication and metrics
    this.deduplication = new RequestDeduplication({
      maxTrackedRequests: 1000,
      requestIdTTL: 5 * 60 * 1000, // 5 minutes
    })

    this.metrics = new TrackingMetrics()

    // Don't initialize immediately - wait for first tracking call
    this.startBatchProcessor()
    this.setupOnlineListener()
  }

  // Lazy getter for API client
  private get apiClient() {
    if (!this._apiClient) {
      this._apiClient = getApiClient()
    }
    return this._apiClient
  }

  // Lazy initialization
  private ensureInitialized() {
    if (!this.initialized) {
      this.initializeTracking()
      this.initialized = true
    }
  }

  /**
   * Initialize persistence layer and load persisted events
   */
  private async initializePersistence(): Promise<void> {
    if (!this.persistence || this.persistenceInitPromise) {
      return
    }

    this.persistenceInitPromise = (async () => {
      try {
        // Load persisted events from IndexedDB
        const persistedEvents = await this.persistence!.load()
        if (persistedEvents.length > 0) {
          console.log(`📦 Loaded ${persistedEvents.length} persisted events from IndexedDB`)

          // ⚠️ CRITICAL: Use batch loading to prevent stack overflow with large event counts
          // unshift(...events) with 300k+ events causes "Maximum call stack size exceeded"
          // Instead, add events in batches to avoid spreading huge arrays
          const BATCH_SIZE = 1000
          for (let i = 0; i < persistedEvents.length; i += BATCH_SIZE) {
            const batch = persistedEvents.slice(i, i + BATCH_SIZE)
            this.queue.unshift(...batch)
          }

          // Clear persistence after loading
          await this.persistence!.clear()
        }
      } catch (error) {
        console.warn('Failed to load persisted events:', error)
        // Continue without persisted events
      }
    })()

    return this.persistenceInitPromise
  }

  private initializeTracking() {
    // Initialize browser fingerprint (async, but we cache the promise)
    // This ensures consistent fingerprint across all events
    this.fingerprintPromise = this.generateFingerprint().catch(err => {
      console.warn('Failed to generate fingerprint:', err)
      // Fallback to simple fingerprint
      this._fingerprint = `fallback-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
      return this._fingerprint
    })

    // Initialize persistence layer (async, non-blocking)
    this.initializePersistence().catch(err => {
      console.warn('Failed to initialize persistence:', err)
    })

    // Initialize UTM parameter capture
    this.captureUTMParameters()

    // Track page performance
    this.trackPagePerformance()

    // Setup error tracking
    this.setupErrorTracking()
  }

  private captureUTMParameters(): Record<string, string> {
    const urlParams = new URLSearchParams(window.location.search)
    const utmParams: Record<string, string> = {}

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
    utmKeys.forEach(key => {
      const value = urlParams.get(key)
      if (value) {
        utmParams[key] = value
        // Store in sessionStorage for persistence
        sessionStorage.setItem(key, value)
      } else {
        // Try to get from sessionStorage
        const stored = sessionStorage.getItem(key)
        if (stored) utmParams[key] = stored
      }
    })

    return utmParams
  }

  /**
   * Generate browser fingerprint for security and tracking
   */
  private async generateFingerprint(): Promise<string> {
    if (this._fingerprint) {
      return this._fingerprint
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('Browser fingerprint', 2, 2)
    }

    // Collect browser characteristics
    const fingerprintData = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      typeof(Worker),
      navigator.platform, //@depricated refactor to navigator.userAgentData.platform
      navigator.cookieEnabled,
      canvas.toDataURL()
    ].join('|')

    // Use crypto.subtle.digest for secure hashing (SHA-256)
    const hashBuffer = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(fingerprintData)
    )

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

    // Store and return the fingerprint (first 16 chars for brevity)
    this._fingerprint = hashHex.substring(0, 16)
    return this._fingerprint
  }

  /**
   * Get the current browser fingerprint
   * Returns the fingerprint if available, or a temporary one if still generating
   * Note: Use getFingerprint() for async access to ensure consistency
   */
  get fingerprint(): string {
    if (!this._fingerprint) {
      // Return temporary fingerprint until real one is ready
      // This is only used as fallback - getFingerprint() should be used for tracking
      return `temp-${Date.now()}`
    }
    return this._fingerprint
  }

  /**
   * Get the browser fingerprint asynchronously
   * Ensures consistent fingerprint across all events
   * @returns Promise that resolves to the fingerprint
   */
  private async getFingerprint(): Promise<string> {
    // If fingerprint is already generated, return it
    if (this._fingerprint) {
      return this._fingerprint
    }

    // If fingerprint generation is in progress, wait for it
    if (this.fingerprintPromise) {
      return this.fingerprintPromise
    }

    // Otherwise generate it now
    return this.generateFingerprint()
  }

  private getBaseEvent(): Partial<BaseTrackingEvent> {
    return {
      timestamp: new Date().toISOString(),
      metadata: {
        source: 'frontend',
        version: '1.0.0',
        pageUrl: window.location.href
      },
    }
  }

  // Public tracking methods
  public trackCTAClick(data: Omit<CTAClickEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as CTAClickEvent)
  }

  public trackOnboardingEvent(data: Omit<OnboardingEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as OnboardingEvent)
  }

  public trackWizardEvent(data: Omit<WizardEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as WizardEvent)
  }

  public trackMonetizationEvent(data: Omit<MonetizationEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as MonetizationEvent)
  }

  public trackEngagementEvent(data: Omit<EngagementEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as EngagementEvent)
  }

  public trackPerformanceEvent(data: Omit<PerformanceEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as PerformanceEvent)
  }

  public trackExperimentEvent(data: Omit<ExperimentEvent, 'timestamp'>) {
    this.ensureInitialized()
    this.track({
      ...this.getBaseEvent(),
      ...data
    } as ExperimentEvent)
  }

  /**
   * Get current tracking metrics
   * @returns Metrics snapshot
   */
  public getMetrics() {
    return this.metrics.getSnapshot()
  }

  /**
   * Log metrics to console
   */
  public logMetrics(): void {
    this.metrics.logMetrics()
  }

  /**
   * Get deduplication statistics
   * @returns Deduplication stats
   */
  public getDeduplicationStats() {
    return this.deduplication.getStats()
  }

  private track(event: TrackingEvent) {
    // Add to queue
    this.queue.push(event)

    // ⚠️ SAFEGUARD: Enforce max queue size to prevent unbounded growth
    // If queue exceeds max size, drop oldest events and force flush
    if (this.queue.length > this.maxQueueSize) {
      console.warn(`⚠️ Queue exceeded max size (${this.maxQueueSize}), dropping oldest events`)
      // Keep only the most recent maxQueueSize events
      this.queue = this.queue.slice(-this.maxQueueSize)
      // Force immediate flush to prevent further accumulation
      this.flush()
    }

    // Record metrics
    this.metrics.recordEventTracked()
    this.metrics.updateQueueSize(this.queue.length)

    // Console log in development
    const isDev = (import.meta as unknown as { env: { DEV: boolean, MODE: string } }).env?.DEV ||
                  (import.meta as unknown as { env: { DEV: boolean, MODE: string } }).env?.MODE === 'development';
    if (isDev) {
      console.log('📊 Tracking Event:', event)
    }

    // Flush if queue is full
    if (this.queue.length >= this.batchSize) {
      this.flush()
    }
  }

  private async flush() {
    if (this.queue.length === 0 || !this.isOnline) return

    // Check circuit breaker - don't send if circuit is open
    if (!this.circuitBreaker.canAttempt()) {
      this.circuitBreaker.recordBlockedRequest()
      this.metrics.updateCircuitBreakerState(this.circuitBreaker.getState())
      return
    }

    // Check retry strategy - don't send if not ready to retry
    const queueKey = 'tracking-batch'
    if (!this.retryStrategy.shouldRetry(queueKey)) {
      return
    }

    const events = [...this.queue]
    this.queue = []

    // Split events into batches of max 100 events (backend limit)
    const MAX_BATCH_SIZE = 100
    const batches: TrackingEvent[][] = []
    for (let i = 0; i < events.length; i += MAX_BATCH_SIZE) {
      batches.push(events.slice(i, i + MAX_BATCH_SIZE))
    }

    // Send each batch separately
    for (const batch of batches) {
      await this.sendBatch(batch, queueKey)
    }
  }

  /**
   * Send a single batch of events (max 100 events)
   * Handles retry logic, circuit breaker, and deduplication
   */
  private async sendBatch(events: TrackingEvent[], queueKey: string) {
    // Phase 3: Generate request ID for deduplication
    const requestId = this.deduplication.generateRequestId()

    try {
      // Get fingerprint asynchronously to ensure consistency
      const fingerprint = await this.getFingerprint()

      // Transform specific event types to generic TrackEventPayload format
      const transformedEvents = events.map(event => this.transformEventToPayload(event, fingerprint))

      // Create abort controller for timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout)

      try {
        // Use batch endpoint for multiple events, single endpoint for one event
        if (transformedEvents.length === 1) {
          await this.apiClient.tracking.trackEvent(transformedEvents[0])
        } else {
          await this.apiClient.tracking.trackEventsBatch({ events: transformedEvents })
        }

        // Success - reset retry and circuit breaker
        this.retryStrategy.recordSuccess(queueKey)
        this.circuitBreaker.recordSuccess()

        // Phase 3: Record successful request
        this.deduplication.recordSentRequest(requestId, events.length, events.map(e => e.eventType))
        this.metrics.recordRequestSent(events.length)
        this.metrics.updateCircuitBreakerState(this.circuitBreaker.getState())

        clearTimeout(timeoutId)
      } catch (error) {
        clearTimeout(timeoutId)
        throw error
      }
    } catch (error) {
      // Record failure in retry strategy and circuit breaker
      const err = error instanceof Error ? error : new Error(String(error))
      this.retryStrategy.recordFailure(queueKey, err)
      this.circuitBreaker.recordFailure()

      // Phase 3: Record failed request
      this.metrics.recordRequestFailed(events.length)
      this.metrics.recordRetryAttempt()
      const cbMetrics = this.circuitBreaker.getMetrics()
      this.metrics.updateCircuitBreakerState(cbMetrics.state, cbMetrics.failureCount)

      // Enhanced error logging with context
      const errorContext = {
        eventCount: events.length,
        eventTypes: events.map(e => e.eventType),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        retryAttempts: this.retryStrategy.getAttempts(queueKey),
        circuitBreakerState: this.circuitBreaker.getState(),
        requestId,
      }

      if (error instanceof Error) {
        console.error('Failed to send tracking events:', {
          message: error.message,
          stack: error.stack,
          name: error.name,
          context: errorContext,
          originalEvents: events
        })
      } else {
        console.error('Failed to send tracking events (unknown error):', {
          error: String(error),
          context: errorContext,
          originalEvents: events
        })
      }

      // Re-add events to queue for retry (only if not max retries exceeded)
      if (!this.retryStrategy.isMaxRetriesExceeded(queueKey)) {
        this.queue.unshift(...events)
        this.metrics.updateQueueSize(this.queue.length)

        // Persist events to IndexedDB for recovery after page reload
        if (this.persistence) {
          try {
            await this.persistence.save(events)
          } catch (persistError) {
            console.warn('Failed to persist events to IndexedDB:', persistError)
            // Continue without persistence - events are still in memory queue
          }
        }
      } else {
        console.warn('Max retries exceeded for tracking batch, dropping events:', {
          eventCount: events.length,
          eventTypes: events.map(e => e.eventType),
        })
      }
    }
  }

  /**
   * Transform specific event types to generic TrackEventPayload format
   * 100% type-safe transformation with no shortcuts or casting
   * Uses async fingerprint to ensure consistency
   */
  private transformEventToPayload(event: TrackingEvent, fingerprint: string): TrackEventPayload {
    // Extract common properties that exist on all tracking events
    const { eventType, timestamp, metadata, ...eventSpecificData } = event

    // Build the base payload with all required and optional fields
    const payload: TrackEventPayload = {
      eventType: eventType,
      pageUrl: window.location.href,
      metadata: {
        // Preserve original metadata
        ...(metadata || {}),
        // Add tracking context
        timestamp,
        originalEventType: eventType,
        sessionFingerprint: fingerprint,
        userAgent: navigator.userAgent,
        screenResolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }

    // Add optional fields only if they have values
    if (document.referrer) {
      payload.referrer = document.referrer
    }

    if (fingerprint) {
      payload.fingerprint = fingerprint
    }

    // Transform event-specific data into the eventData field
    // This preserves all type-specific properties (ctaType, stepNumber, etc.)
    const eventDataKeys = Object.keys(eventSpecificData)
    if (eventDataKeys.length > 0) {
      // Type-safe transformation: we know eventSpecificData contains the event-specific properties
      // These are all serializable values (string, number, boolean, etc.)
      const eventData: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(eventSpecificData)) {
        // Only include serializable values
        if (value !== undefined && value !== null && typeof value !== 'function') {
          eventData[key] = value
        }
      }
      if (Object.keys(eventData).length > 0) {
        payload.eventData = eventData
      }
    }

    return payload
  }

  private startBatchProcessor() {
    setInterval(() => {
      this.flush()
    }, this.flushInterval)
  }

  private setupOnlineListener() {
    window.addEventListener('online', () => {
      this.isOnline = true
      this.flush()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
    })
  }

  private trackPagePerformance() {
    if ('performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          try {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
            const paint = performance.getEntriesByType('paint')

            const fcp = paint.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0

            this.trackPerformanceEvent({
              eventType: TrackingEventType.PAGE_PERFORMANCE,
              loadTime: navigation.loadEventEnd - navigation.loadEventStart,
              metadata: {
                pageName: window.location.pathname,
                firstContentfulPaint: fcp
              }
            })
          } catch (error) {
            // Silently fail if Pinia is not available (e.g., during tests)
            console.warn('Performance tracking failed - Pinia not available:', error)
          }
        }, 0)
      })
    }
  }

  private setupErrorTracking() {
    window.addEventListener('error', (event) => {
      // Only track if we have an active Pinia instance
      try {
        this.trackPerformanceEvent({
          eventType: TrackingEventType.ERROR_OCCURRED,
          errorMessage: event.message,
          errorStack: event.error?.stack,
          metadata: {
            errorType: 'javascript',
            pageName: window.location.pathname,
            userAction: 'page_load'
          }
        })
      } catch (error) {
        // Silently fail if Pinia is not available (e.g., during tests)
        console.warn('Error tracking failed - Pinia not available:', error)
      }
    })

    window.addEventListener('unhandledrejection', (event) => {
      // Only track if we have an active Pinia instance
      try {
        this.trackPerformanceEvent({
          eventType: TrackingEventType.ERROR_OCCURRED,
          errorMessage: event.reason?.toString() || 'Unhandled promise rejection',
          errorStack: event.reason?.stack,
          metadata: {
            errorType: 'javascript',
            pageName: window.location.pathname,
            userAction: 'async_operation'
          }
        })
      } catch (error) {
        // Silently fail if Pinia is not available (e.g., during tests)
        console.warn('Error tracking failed - Pinia not available:', error)
      }
    })
  }
}

// Lazy singleton instance
let _trackingService: TrackingService | null = null

function getTrackingService(): TrackingService {
  if (!_trackingService) {
    _trackingService = new TrackingService()
  }
  return _trackingService
}

// Export for direct access (use sparingly)
export const trackingService = {
  get instance() {
    return getTrackingService()
  }
}

// Vue composable
export function useTracking() {
  const service = getTrackingService()
  return {
    trackCTAClick: service.trackCTAClick.bind(service),
    trackOnboardingEvent: service.trackOnboardingEvent.bind(service),
    trackWizardEvent: service.trackWizardEvent.bind(service),
    trackMonetizationEvent: service.trackMonetizationEvent.bind(service),
    trackEngagementEvent: service.trackEngagementEvent.bind(service),
    trackPerformanceEvent: service.trackPerformanceEvent.bind(service),
    trackExperimentEvent: service.trackExperimentEvent.bind(service)
  }
}
